/* eslint-disable max-lines-per-function, max-statements */
import { useDeepCompareMemoize } from "@bluframe/mightychroma-mightytool-ui";
import useReviews from "hooks/useReviews";
import { useCallback, useMemo, useState } from "react";
const DEFAULT_IS_MODAL_OPEN = false;
const EMPTY_TECH_SPECS_SELECTED = [];
const useTechSpecsSelector = () => {
    const { setTechSpecs, state } = useReviews();
    const [isModalOpen, setIsModalOpen] = useState(DEFAULT_IS_MODAL_OPEN);
    const onModalClose = useCallback(() => {
        setIsModalOpen(false);
    }, [setIsModalOpen]);
    const onSelectClick = useCallback(() => {
        setIsModalOpen(true);
    }, [setIsModalOpen]);
    const onSetTechSpecs = useCallback((techSpecs) => {
        if (techSpecs) {
            setTechSpecs(techSpecs);
        }
    }, [setTechSpecs]);
    const modal = useMemo(() => ({
        isOpen: isModalOpen,
        name: "Tech Specs Selector",
        onClose: onModalClose
    }), [isModalOpen, onModalClose]);
    const techSpecsSelected = useMemo(() => state.homeVideoReview.techSpecs ?? EMPTY_TECH_SPECS_SELECTED, [state.homeVideoReview.techSpecs]);
    const avForumsTechSpecs = state.avForums.selected.review.techSpecs;
    const avNirvanaTechSpecs = state.avNirvana.selected.review.techSpecs;
    const avsForumTechSpecs = state.avsForum.selected.review.techSpecs;
    const bluRayTechSpecs = state.bluRay.selected.review.techSpecs;
    const bluRayAuthorityTechSpecs = state.bluRayAuthority.selected.review.techSpecs;
    const digitalBitsTechSpecs = state.digitalBits.selected.review.techSpecs;
    const doBluTechSpecs = state.doBlu.selected.review.techSpecs;
    const hidefDigestTechSpecs = state.hidefDigest.selected.review.techSpecs;
    const highDefDiscNewsTechSpecs = state.highDefDiscNews.selected.review.techSpecs;
    const homeTheaterForumTechSpecs = state.homeTheaterForum.selected.review.techSpecs;
    const theaterByteTechSpecs = state.theaterByte.selected.review.techSpecs;
    const whySoBluTechSpecs = state.whySoBlu.selected.review.techSpecs;
    /**
     * Build the "available tech specs" list for the selector modal.
     * Keeping this stable matters because ref churn here cascades into card rerenders.
     */
    const techSpecsAvailable = useMemo(() => [
        {
            serviceName: "avForums",
            techSpecs: avForumsTechSpecs ?? EMPTY_TECH_SPECS_SELECTED
        },
        {
            serviceName: "avNirvana",
            techSpecs: avNirvanaTechSpecs ?? EMPTY_TECH_SPECS_SELECTED
        },
        {
            serviceName: "avsForum",
            techSpecs: avsForumTechSpecs ?? EMPTY_TECH_SPECS_SELECTED
        },
        {
            serviceName: "bluRay",
            techSpecs: bluRayTechSpecs ?? EMPTY_TECH_SPECS_SELECTED
        },
        {
            serviceName: "bluRayAuthority",
            techSpecs: bluRayAuthorityTechSpecs ?? EMPTY_TECH_SPECS_SELECTED
        },
        {
            serviceName: "digitalBits",
            techSpecs: digitalBitsTechSpecs ?? EMPTY_TECH_SPECS_SELECTED
        },
        {
            serviceName: "doBlu",
            techSpecs: doBluTechSpecs ?? EMPTY_TECH_SPECS_SELECTED
        },
        {
            serviceName: "hidefDigest",
            techSpecs: hidefDigestTechSpecs ?? EMPTY_TECH_SPECS_SELECTED
        },
        {
            serviceName: "highDefDiscNews",
            techSpecs: highDefDiscNewsTechSpecs ?? EMPTY_TECH_SPECS_SELECTED
        },
        {
            serviceName: "homeTheaterForum",
            techSpecs: homeTheaterForumTechSpecs ?? EMPTY_TECH_SPECS_SELECTED
        },
        {
            serviceName: "theaterByte",
            techSpecs: theaterByteTechSpecs ?? EMPTY_TECH_SPECS_SELECTED
        },
        {
            serviceName: "whySoBlu",
            techSpecs: whySoBluTechSpecs ?? EMPTY_TECH_SPECS_SELECTED
        }
    ], [
        avForumsTechSpecs,
        avNirvanaTechSpecs,
        avsForumTechSpecs,
        bluRayTechSpecs,
        bluRayAuthorityTechSpecs,
        digitalBitsTechSpecs,
        doBluTechSpecs,
        hidefDigestTechSpecs,
        highDefDiscNewsTechSpecs,
        homeTheaterForumTechSpecs,
        theaterByteTechSpecs,
        whySoBluTechSpecs
    ]);
    const stableTechSpecsAvailable = useDeepCompareMemoize(techSpecsAvailable);
    return useMemo(() => ({
        modal,
        onSelectClick,
        onSetTechSpecs,
        techSpecsAvailable: stableTechSpecsAvailable,
        techSpecsSelected
    }), [
        modal,
        onSelectClick,
        onSetTechSpecs,
        stableTechSpecsAvailable,
        techSpecsSelected
    ]);
};
export default useTechSpecsSelector;
